import { useRef } from "react";

export const ADD_NEW = Symbol.for("add-new");

/**
 * Default function to convert an item to a string representation
 * @template Item - The type of the item
 * @param item - The item to convert to string
 * @returns String representation of the item or empty string if null/undefined
 */
export const defaultItemToString = function <Item>(item: Item | null) {
  return item != null ? String(item) : "";
};

/**
 * Default function to generate a unique key for an item
 * @template Item - The type of the item
 * @param item - The item to generate a key for
 * @returns The item itself as the key
 */
export const defaultItemToKey = function <Item>(item: Item | null) {
  return item;
};

/**
 * Hook to handle downshift environment configuration for shadow DOM support
 *
 * Features:
 * - Fixes shadow DOM compatibility issues with downshift
 * - Provides proper document and event listener references
 * - Returns ref for wrapper element and environment configuration
 *
 * @returns Object containing ref and environment configuration
 *
 * @example
 * const { ref, environment } = useDownshiftEnvironment();
 *
 * return (
 *   <div ref={ref}>
 *     <Combobox environment={environment}>
 *       combobox content
 *     </Combobox>
 *   </div>
 * );
 */
export const useDownshiftEnvironment = () => {
  // fix for shadow DOM, see https://gist.github.com/Rendez/1dd55882e9b850dd3990feefc9d6e177
  const wrapperDivRef = useRef<HTMLDivElement | null>(null);

  const shadowRoot = wrapperDivRef.current
    ? wrapperDivRef.current.getRootNode()
    : null;

  const shadowDocument =
    wrapperDivRef.current && shadowRoot ? shadowRoot.ownerDocument : null;

  const environment = shadowDocument
    ? {
        document: shadowDocument,
        addEventListener: shadowDocument.addEventListener.bind(shadowRoot),
        removeEventListener:
          shadowDocument.removeEventListener.bind(shadowRoot),
        Node,
      }
    : undefined;

  return { ref: wrapperDivRef, environment };
};
